import type { ReactNode } from "react";
import type { AssetType } from "@klantpunt/shared";

/**
 * Eigen Klantpunt-symbolen per assettype (docs/SYMBOLEN.md). Getekend op een raster van 24 × 24 met
 * lijndikte 2 en ronde uiteinden, net als de lucide-iconen in de rest van de app. Geen NEN-, ISO- of
 * IEC-symbolen overnemen; brand- en vluchtvoorzieningen volgen wel de bekende vormen van de
 * veiligheidsborden. Elk symbool heeft één betekenis.
 */
const PATHS: Record<AssetType, ReactNode> = {
  heating_boiler: (
    <>
      <rect x="5" y="2" width="14" height="16" rx="2" />
      <path d="M12 6.5c1.6 1.6 2.4 2.8 2.4 4.1a2.4 2.4 0 0 1-4.8 0c0-.9.4-1.6 1-2.2.3.6.7.9 1.2.9 0-1.2-.2-1.9.2-2.8z" />
      <path d="M8 18v4" />
      <path d="M12 18v4" />
      <path d="M16 18v4" />
    </>
  ),
  heat_pump: (
    <>
      <rect x="2" y="5" width="20" height="13" rx="1.5" />
      <circle cx="9" cy="11.5" r="4" />
      <circle cx="9" cy="11.5" r="1" />
      <path d="M15.5 8.5h3.5" />
      <path d="M15.5 11.5h3.5" />
      <path d="M15.5 14.5h3.5" />
      <path d="M5 18v2.5" />
      <path d="M19 18v2.5" />
    </>
  ),
  air_conditioner: (
    <>
      <rect x="2" y="4" width="20" height="8" rx="2" />
      <path d="M5 9.5h14" />
      <path d="M7 15.5c0 1.5-1 2-1 3.5" />
      <path d="M12 15.5c0 1.5-1 2-1 3.5" />
      <path d="M17 15.5c0 1.5-1 2-1 3.5" />
    </>
  ),
  radiator: (
    <>
      <rect x="3" y="5" width="18" height="13" rx="1.5" />
      <path d="M7.5 5v13" />
      <path d="M12 5v13" />
      <path d="M16.5 5v13" />
      <path d="M5 18v2" />
      <path d="M19 18v2" />
      <path d="M1.5 8H3" />
    </>
  ),
  chiller: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="1.5" />
      <path d="M12 8v8" />
      <path d="m8.5 10 7 4" />
      <path d="m15.5 10-7 4" />
      <path d="M5 19v2" />
      <path d="M19 19v2" />
    </>
  ),
  buffer_tank: (
    <>
      <ellipse cx="12" cy="5" rx="7" ry="2.5" />
      <path d="M5 5v14c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V5" />
      <path d="M5 12c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5" />
    </>
  ),
  ventilation_unit: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="1.5" />
      <path d="M12 7.5 16.5 12 12 16.5 7.5 12z" />
      <path d="M2 9h3.5" />
      <path d="M18.5 15H22" />
      <path d="M2 15h3.5" />
      <path d="M18.5 9H22" />
    </>
  ),
  exhaust_fan: (
    <>
      <path d="M2 21h20" />
      <path d="M8 21v-6h8v6" />
      <path d="M4.5 15C5 9 8 7 12 7s7 2 7.5 8z" />
      <path d="M12 7V4" />
      <path d="M9.5 11.5h5" />
    </>
  ),
  vent_grille: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="m7 10 2-3" />
      <path d="m7 14 5-7" />
      <path d="m7 17.5 7-10.5" />
      <path d="m10.5 17 6.5-10" />
      <path d="m14 17 3-4.5" />
    </>
  ),
  sprinkler: (
    <>
      <path d="M12 2v2.5" />
      <path d="M8.5 4.5h7v3h-7z" />
      <path d="M9 7.5 11 17" />
      <path d="M15 7.5 13 17" />
      <path d="M12 10.5v3" />
      <path d="M5 17.5h14" />
      <path d="M5 21l1-1.5" />
      <path d="M12 21.5v-2" />
      <path d="M19 21l-1-1.5" />
    </>
  ),
  fire_hose_reel: (
    <>
      <circle cx="10" cy="10" r="7" />
      <circle cx="10" cy="10" r="2.5" />
      <path d="M10 17c4 0 8 0 9.5 2.5" />
      <path d="m18 21.5 3-3" />
    </>
  ),
  fire_extinguisher: (
    <>
      <path d="M9 8h6v12.5a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 20.5z" />
      <path d="M10.5 8V5h3v3" />
      <path d="M13.5 5h4l2 2" />
      <path d="M10.5 5c-3 0-4.5 2-4.5 5v4" />
      <path d="M9 13h6" />
    </>
  ),
  smoke_vent: (
    <>
      <path d="M2 20 12 12l10 8" />
      <path d="m12 12 3.5-5.5" />
      <path d="M8 8V2.5" />
      <path d="M5.5 5 8 2.5 10.5 5" />
    </>
  ),
  smoke_detector: (
    <>
      <path d="M3 4h18" />
      <path d="M5 4v2a7 7 0 0 0 14 0V4" />
      <circle cx="12" cy="8" r="1" />
      <path d="M8 17c0 1.5-1 2.5-1 4" />
      <path d="M12 17c0 1.5-1 2.5-1 4" />
      <path d="M16 17c0 1.5-1 2.5-1 4" />
    </>
  ),
  fire_alarm_panel: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <rect x="6.5" y="6.5" width="11" height="4" rx=".5" />
      <circle cx="7.5" cy="15" r="1" />
      <circle cx="11" cy="15" r="1" />
      <path d="M14.5 15h3" />
      <path d="M14.5 18h3" />
    </>
  ),
  access_control: (
    <>
      <rect x="6" y="2" width="12" height="20" rx="2" />
      <path d="M9.5 9.5a3.5 3.5 0 0 1 5 0" />
      <path d="M8 7.5a6 6 0 0 1 8 0" />
      <circle cx="12" cy="12" r=".75" />
      <path d="M10 17.5h4" />
    </>
  ),
  camera: (
    <>
      <path d="M3 4v6" />
      <path d="M3 7h3" />
      <path d="m6 5 13-1.5 2 5-13 4z" />
      <circle cx="17" cy="6.5" r="1" />
      <path d="M11 12v5h-4" />
    </>
  ),
  emergency_light: (
    <>
      <rect x="3" y="4" width="18" height="8" rx="1.5" />
      <path d="M7 8h3" />
      <path d="M8.5 6.5v3" />
      <path d="M14 8h3" />
      <path d="M6 15.5 4.5 19" />
      <path d="M12 15.5V19.5" />
      <path d="M18 15.5l1.5 3.5" />
    </>
  ),
  exit_sign: (
    <>
      <path d="M13 3H5v18h8" />
      <path d="M10 12h11" />
      <path d="m17 8 4 4-4 4" />
    </>
  ),
  main_switchboard: (
    <>
      <rect x="2" y="3" width="20" height="18" rx="2" />
      <path d="M12 3v18" />
      <path d="M8 7 5.5 12H9l-2.5 5" />
      <path d="M15 8h3.5" />
      <path d="M15 12h3.5" />
      <path d="M15 16h3.5" />
    </>
  ),
  distribution_board: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 12h18" />
      <path d="M6.5 6.5v3" />
      <path d="M10 6.5v3" />
      <path d="M13.5 6.5v3" />
      <path d="M17 6.5v3" />
      <path d="M6.5 14.5v3" />
      <path d="M10 14.5v3" />
      <path d="M13.5 14.5v3" />
    </>
  ),
  solar_panels: (
    <>
      <path d="M5 9h16l-3 11H2z" />
      <path d="M3.5 14.5h16" />
      <path d="M10 9l-3 11" />
      <path d="M15.5 9l-3 11" />
      <circle cx="19" cy="3.5" r="1.5" />
    </>
  ),
  generator: (
    <>
      <rect x="2" y="7" width="20" height="12" rx="1.5" />
      <circle cx="9" cy="13" r="3.5" />
      <path d="M7.3 13.3c.6-1.6 1.5-1.6 1.9 0s1.3 1.6 1.9 0" />
      <path d="M15 10.5h4" />
      <path d="M15 13h4" />
      <path d="M18 7V3h3" />
      <path d="M4 19v2" />
      <path d="M20 19v2" />
    </>
  ),
  transformer: (
    <>
      <circle cx="9" cy="12" r="5.5" />
      <circle cx="15" cy="12" r="5.5" />
    </>
  ),
  lightning_rod: (
    <>
      <path d="M2 21 12 13l10 8" />
      <path d="M12 13V4" />
      <path d="M9 2.5h6" />
      <path d="m18 4-2 4h3l-2 4" />
    </>
  ),
  intercom: (
    <>
      <rect x="6" y="2" width="12" height="20" rx="2" />
      <path d="M9.5 6h5" />
      <path d="M9.5 8.5h5" />
      <path d="M9.5 11h5" />
      <circle cx="12" cy="16.5" r="2" />
    </>
  ),
  data_cabinet: (
    <>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M4 7.5h16" />
      <path d="M4 12.5h16" />
      <path d="M4 17.5h16" />
      <circle cx="7" cy="5" r=".5" />
      <circle cx="7" cy="10" r=".5" />
      <circle cx="7" cy="15" r=".5" />
    </>
  ),
  lift: (
    <>
      <rect x="4" y="2" width="16" height="20" rx="1.5" />
      <path d="M12 2v20" />
      <path d="m6.5 9.5 2-3 2 3z" />
      <path d="m13.5 14.5 2 3 2-3z" />
    </>
  ),
  overhead_door: (
    <>
      <path d="M2 21V3h20v18" />
      <rect x="5" y="6" width="14" height="15" />
      <path d="M5 9.75h14" />
      <path d="M5 13.5h14" />
      <path d="M5 17.25h14" />
    </>
  ),
  water_heater: (
    <>
      <rect x="6" y="5" width="12" height="17" rx="4" />
      <path d="M9.5 2v3" />
      <path d="M14.5 2v3" />
      <path d="M12 10v6" />
      <circle cx="12" cy="17.5" r="1.5" />
    </>
  ),
  pump: (
    <>
      <circle cx="11" cy="13" r="7" />
      <path d="m8 9.5 7 3.5-7 3.5z" />
      <path d="M11 6V3" />
      <path d="M8 3h6" />
      <path d="M18 13h4" />
    </>
  ),
  facade_access: (
    <>
      <path d="M3 22V6h9" />
      <path d="M8 6V3h12" />
      <path d="M11 3v8" />
      <path d="M19 3v8" />
      <path d="M9 11h12v4H9z" />
      <path d="M3 10h3" />
      <path d="M3 14h3" />
      <path d="M3 18h3" />
    </>
  ),
};

/** Symbool van een assettype; kleur via currentColor. */
export function AssetTypeSymbol({ type, size = 18 }: { type: AssetType; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[type]}
    </svg>
  );
}
